import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule, MatOptionModule } from '@angular/material/core';
import { MatMenuModule } from '@angular/material/menu'; 

import { NgxChartsModule, Color, ScaleType } from '@swimlane/ngx-charts';

import { forkJoin } from 'rxjs';
import { MenuComponent } from '../../shared/components/menu/menu.component';
import { FooterComponent } from '../../shared/components/footer/footer.component';
import { LoginValidationComponent } from '../../book-create/login-validation.component/login-validation.component';
import { AlertService, AlertType } from '../../shared/services/alert.service';
import { BookSalesAnalyticsService } from '../../shared/services/apis/platform/book-sales-analytics.service';
import { BookSalesAnalyticsFilterRequest } from '../../shared/services/requests/platform/book-sales-analytics-filter.request';
import { BookSalesAnalyticsFunnelResponse } from '../../shared/services/responses/platform/book-sales-analytics-funnel.response';
import { BookSalesAnalyticsSalesByItemResponse } from '../../shared/services/responses/platform/book-sales-analytics-sales-by-item.response';
import { BookSalesAnalyticsSalesByKitResponse } from '../../shared/services/responses/platform/book-sales-analytics-sales-by-kit.response';
import { BookSalesAnalyticsSoldListResponse } from '../../shared/services/responses/platform/book-sales-analytics-sold-list.response';
import { BookSalesAnalyticsSummaryResponse } from '../../shared/services/responses/platform/book-sales-analytics-summary.response';
import { BookSalesAnalyticsTimeSeriesResponse } from '../../shared/services/responses/platform/book-sales-analytics-time-series.response';
import { KitOptionItem, KitOptionResponse } from '../../shared/services/responses/platform/kit-option.response';
import { MatSelectModule } from '@angular/material/select';
import { PublishedBookItem } from '../../shared/services/responses/platform/published-books.response';

@Component({
    selector: 'app-book-sales-analytics',
    standalone: true,
    templateUrl: './book-sales-analytics.component.html',
    styleUrl: './book-sales-analytics.component.css',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MenuComponent,
        FooterComponent,
        LoginValidationComponent,
        MatIconModule,
        MatTooltipModule,
        MatProgressSpinnerModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatSelectModule,
        MatOptionModule,
        NgxChartsModule,
        MatMenuModule,
        MatIconModule,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class BookSalesAnalyticsComponent implements OnInit {
    isLoading = false;
    kitOptions: KitOptionItem[] = [];
    publishedBooks: PublishedBookItem[] = [];
    lineScheme: Color = {
        name: 'custom',
        selectable: true,
        group: ScaleType.Ordinal,
        domain: ['#85CF7A', '#E4636F'], // verde (views) e vermelho (compras)
    };
    // filtros
    form!: FormGroup;
    idBookVersion!: number;

    bookVersionName!: string;
    bookTitle!: string;

    // KPIs
    kpiSales = 0;
    kpiViews = 0;
    kpiCoupons = 0;
    kpiGross = 0;
    kpiNet = 0;
    kpiConversionRate = 0; // 0..1
    kpiViewsPerSale: number | null = null;
    itemsMax = 0;
    kitsMax = 0;

    // Funil
    funnelMax = 0;
    funnelView = 0;
    funnelClick = 0;
    funnelPurchase = 0;

    // Gráficos ngx-charts
    lineSeries: any[] = [];            // Visualizações x Compras ao longo do tempo
    barByKit: any[] = [];              // Vendas por kit
    barByItem: any[] = [];             // Vendas por item

    // Tabela de vendas
    soldRows: Array<{ saleDate: string; value: number; city?: string | null; state?: string | null }> = [];
    page = 1;
    pageSize = 20;
    totalRows = 0;

    // opções ngx-charts
    viewLine: [number, number] = [900, 300];
    viewBar: [number, number] = [900, 280];
    showLegend = true;
    showXAxis = true;
    showYAxis = true;
    showXAxisLabel = true;
    showYAxisLabel = true;
    xAxisLabelLine = 'Dia';
    yAxisLabelLine = 'Qtde';
    xAxisLabelBarKit = 'Kit';
    yAxisLabelBarKit = 'Vendas';
    xAxisLabelBarItem = 'Item';
    yAxisLabelBarItem = 'Vendas';
    autoScale = true;
    animations = true;

    constructor(
        private fb: FormBuilder,
        private route: ActivatedRoute,
        private analyticsSrv: BookSalesAnalyticsService,
        private alert: AlertService,
        private cdr: ChangeDetectorRef,
        private router: Router
    ) { }

    ngOnInit(): void {
        // id da versão vem por rota: /analytics/:idBookVersion
        const id = Number(this.route.snapshot.paramMap.get('idBookVersion'));
        if (!id || Number.isNaN(id)) {
            this.alert.show('Erro', 'Versão do livro inválida.', AlertType.error);
            return;
        }
        this.idBookVersion = id;

        this.form = this.fb.group({
            idKit: [null],           // opcional
            dateStart: [null],       // Date
            dateEnd: [null],         // Date
            paidStatusIds: [null]    // number[] opcional
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    onLoginValidated(isLogged: boolean) {
        if (!isLogged) return;

        this.analyticsSrv.getKits(this.idBookVersion).subscribe({
            next: (r: KitOptionResponse) => {
                if (!r.isError) this.kitOptions = r.items || [];
                this.loadAll();
            },
            error: () => this.loadAll()
        });

        this.loadPublishedBooks();

        this.loadAll();
    }


    private loadPublishedBooks(): void {
        this.analyticsSrv.getPublishedBooks().subscribe({
            next: r => { if (!r.isError) this.publishedBooks = r.items || []; },
            error: _ => { this.publishedBooks = []; }
        });
    }

    get totalPages(): number {
        return Math.max(1, Math.ceil((this.totalRows || 0) / (this.pageSize || 1)));
    }

    private buildRequest(overrides?: Partial<BookSalesAnalyticsFilterRequest>): BookSalesAnalyticsFilterRequest {
        const { idKit, dateStart, dateEnd, paidStatusIds } = this.form.getRawValue();
        return {
            idBookVersion: this.idBookVersion,
            idKit: idKit ? Number(idKit) : null,
            dateStart: dateStart ? new Date(dateStart) : null,
            dateEnd: dateEnd ? new Date(dateEnd) : null,
            paidStatusIds: (Array.isArray(paidStatusIds) && paidStatusIds.length) ? paidStatusIds.map((n: any) => Number(n)) : null,
            page: this.page,
            pageSize: this.pageSize,
            ...(overrides || {})
        };
    }

    applyFilters() {
        this.page = 1;
        this.loadAll();
    }

    clearFilters() {
        this.form.reset({ idKit: null, dateStart: null, dateEnd: null, paidStatusIds: null });
        this.page = 1;
        this.loadAll();
    }

    pageChange(nextPage: number) {
        if (nextPage < 1) return;
        const totalPages = Math.max(1, Math.ceil(this.totalRows / this.pageSize));
        if (nextPage > totalPages) return;
        this.page = nextPage;
        this.loadSoldOnly();
    }

    private loadAll() {
        this.isLoading = true;
        const req = this.buildRequest();

        forkJoin({
            summary: this.analyticsSrv.summary(req),
            funnel: this.analyticsSrv.funnel(req),
            series: this.analyticsSrv.timeSeries(req),
            byKit: this.analyticsSrv.salesByKit(req),
            byItem: this.analyticsSrv.salesByItem(req),
            sold: this.analyticsSrv.soldList(req)
        }).subscribe({
            next: (res) => {
                // Summary
                const s: BookSalesAnalyticsSummaryResponse = res.summary;
                if (s.isError) throw new Error(s.errorDescription || 'Erro no summary.');
                this.kpiSales = s.sales ?? 0;
                this.kpiViews = s.views ?? 0;
                this.kpiCoupons = s.couponUses ?? 0;
                this.kpiGross = s.grossAmount ?? 0;
                this.kpiNet = s.netAmount ?? 0;
                this.kpiConversionRate = s.conversionRate ?? 0;
                this.kpiViewsPerSale = s.viewsPerSale ?? null;
                this.bookVersionName = s.versionName ?? '';
                this.bookTitle = s.bookTitle ?? '';

                // Funnel
                const f: BookSalesAnalyticsFunnelResponse = res.funnel;
                if (f.isError) throw new Error(f.errorDescription || 'Erro no funnel.');
                this.funnelView = f.viewCount ?? 0;
                this.funnelClick = f.clickCount ?? 0;
                this.funnelPurchase = f.purchaseCount ?? 0;
                this.funnelMax = Math.max(this.kpiViews || 0, this.funnelClick || 0, this.funnelPurchase || 0);

                // Time series
                const t: BookSalesAnalyticsTimeSeriesResponse = res.series;
                if (t.isError) throw new Error(t.errorDescription || 'Erro no timeseries.');
                this.lineSeries = [
                    {
                        name: 'Visualizações',
                        series: (t.points || []).map(p => ({ name: this.formatDate(p.date), value: p.views }))
                    },
                    {
                        name: 'Compras',
                        series: (t.points || []).map(p => ({ name: this.formatDate(p.date), value: p.purchases }))
                    }
                ];

                // Sales by kit
                const k: BookSalesAnalyticsSalesByKitResponse = res.byKit;
                if (k.isError) throw new Error(k.errorDescription || 'Erro em vendas por kit.');
                this.barByKit = (k.items || []).map(i => ({
                    name: i.kitName || `Kit ${i.idKit}`,
                    value: i.quantity
                }));
                this.kitsMax = Math.max(...this.barByKit.map(b => b.value), 0);

                // Sales by item
                const it: BookSalesAnalyticsSalesByItemResponse = res.byItem;
                if (it.isError) throw new Error(it.errorDescription || 'Erro em vendas por item.');
                this.barByItem = (it.items || []).map(i => ({ name: i.itemName, value: i.quantity }));
                this.itemsMax = Math.max(...(this.barByItem.map(b => b.value)), 0);

                // Sold list
                const sl: BookSalesAnalyticsSoldListResponse = res.sold;
                if (sl.isError) throw new Error(sl.errorDescription || 'Erro ao listar vendas.');
                this.totalRows = sl.totalRows || 0;
                this.page = sl.page || 1;
                this.pageSize = sl.pageSize || 20;
                this.soldRows = (sl.rows || []).map(r => ({
                    saleDate: this.formatDate(r.saleDate),
                    value: r.value,
                    city: r.city,
                    state: r.state
                }));

                this.isLoading = false;
                this.cdr.markForCheck();
            },
            error: (err) => {
                this.isLoading = false;
                this.alert.show('Erro', 'Falha ao carregar dados de análise.', AlertType.error);
                this.cdr.markForCheck();
            }
        });
    }

    private loadSoldOnly() {
        const req = this.buildRequest({ page: this.page, pageSize: this.pageSize });
        this.analyticsSrv.soldList(req).subscribe({
            next: (sl) => {
                if (sl.isError) {
                    this.alert.show('Erro', sl.errorDescription || 'Erro ao listar vendas.', AlertType.error);
                    return;
                }
                this.totalRows = sl.totalRows || 0;
                this.page = sl.page || this.page;
                this.pageSize = sl.pageSize || this.pageSize;
                this.soldRows = (sl.rows || []).map(r => ({
                    saleDate: this.formatDate(r.saleDate),
                    value: r.value,
                    city: r.city,
                    state: r.state
                }));
                this.cdr.markForCheck();
            },
            error: () => {
                this.alert.show('Erro', 'Erro ao atualizar a lista de vendas.', AlertType.error);
            }
        });
    }

    funnelPct(v: number | null | undefined): number {
        if (!this.funnelMax || !v) return 0;
        return (v / this.funnelMax) * 100;
    }

    pct(v: number | null | undefined, max: number): number {
        if (!max || !v) return 0;
        return (v / max) * 100;
    }

    voltar() {
        this.router.navigate(['../meus-livros']);
    }

    goToBookVersion(idBookVersion: number) {
        this.router.navigate(['/relatorio', idBookVersion]);
        //recarregar dados
        this.idBookVersion = idBookVersion;
        this.loadAll();
    }

    // util
    private formatDate(d: any): string {
        const dt = new Date(d);
        // dd/MM
        return dt.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
    }
}
